// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/lib/googlePlaces.ts — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
/**
 * Google Places (New) client — fallback address autocomplete (core).
 *
 * Fires ONLY when Canada Post's AddressComplete returns zero top-level results
 * (its dataset misses brand-new streets). Same privacy posture as
 * addressComplete.ts: each query sends ONLY the typed address fragment — no
 * name, DOB, or health data. The API key (cfg key `google_places_key`) comes
 * from the app's lookups endpoint, bound in the per-app adapter (physician:
 * `/api/config/lookups`; patient: `/api/bridge/config/lookups`) — never
 * hardcoded here — so it rotates without an SPA rebuild; when no key is
 * configured the fallback is silently OFF and behavior is byte-identical to
 * Canada-Post-only. Key fetching lives in the adapters (via
 * makeLookupKeyFetcher in ./lookupsKey); this core is endpoint-free.
 *
 * Every network call is wrapped so any failure (network, quota, bad key,
 * malformed response) degrades to [] / null — never a thrown error — leaving
 * the existing manual-entry hint in place.
 *
 * Session pricing: a session token spans one typing session and is consumed by
 * the Place Details (retrieve) call, which makes the autocomplete requests free
 * when the session completes with a details fetch.
 */

export interface GoogleAddressSuggestion {
  id: string;
  text: string;
  description: string;
  isRetrievable: boolean;
  isGoogle: boolean;
}

export interface GoogleAddressRecord {
  address: string;
  suite: string;
  city: string;
  province: string;
  postal_code: string;
}

const GP_AUTOCOMPLETE = "https://places.googleapis.com/v1/places:autocomplete";
const GP_DETAILS = "https://places.googleapis.com/v1/places";

// Nova Scotia bounding box — a bias (not a restriction), so out-of-province
// addresses remain findable, they just rank lower.
const NS_LOCATION_BIAS = {
  rectangle: {
    low: { latitude: 43.3, longitude: -66.5 },
    high: { latitude: 47.1, longitude: -59.6 },
  },
};

/**
 * A session token spans one typing session and is consumed by the details call.
 * crypto.randomUUID() where available, Math.random fallback otherwise.
 */
export function newSessionToken(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* fall through to Math.random */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Autocomplete a typed fragment via Google Places (New).
 * Returns [{id, text, description, isRetrievable, isGoogle}] (capped ~6).
 * Returns [] on ANY error/empty so the caller degrades cleanly.
 */
export async function findAddressesGoogle(key: string, searchTerm: string, sessionToken: string): Promise<GoogleAddressSuggestion[]> {
  try {
    const r = await fetch(GP_AUTOCOMPLETE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": key,
      },
      body: JSON.stringify({
        input: searchTerm,
        sessionToken,
        includedRegionCodes: ["ca"],
        locationBias: NS_LOCATION_BIAS,
      }),
    });
    if (!r.ok) return [];
    const data = await r.json();
    const suggestions = (data && data.suggestions) || [];
    return suggestions
      .map((s: any): GoogleAddressSuggestion | null => {
        const p = s && s.placePrediction;
        if (!p) return null;
        const sf = p.structuredFormat || {};
        return {
          id: p.placeId,
          text: (sf.mainText && sf.mainText.text) || (p.text && p.text.text) || "",
          description: (sf.secondaryText && sf.secondaryText.text) || "",
          isRetrievable: true,
          isGoogle: true,
        };
      })
      .filter((x: GoogleAddressSuggestion | null): x is GoogleAddressSuggestion => !!(x && x.id))
      .slice(0, 6);
  } catch (_e) {
    return [];
  }
}

/**
 * Retrieve the full record for a Google place.
 * Returns {address, suite, city, province, postal_code} or null on any error.
 */
export async function retrieveAddressGoogle(key: string, placeId: string, sessionToken: string): Promise<GoogleAddressRecord | null> {
  try {
    const url = `${GP_DETAILS}/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(sessionToken)}`;
    const r = await fetch(url, {
      method: "GET",
      headers: {
        "X-Goog-Api-Key": key,
        "X-Goog-FieldMask": "addressComponents",
      },
    });
    if (!r.ok) return null;
    const data = await r.json();
    const components = (data && data.addressComponents) || [];
    return parseAddressComponents(components);
  } catch (_e) {
    return null;
  }
}

/**
 * Map Google addressComponents → the SAME shape Canada Post produces:
 * {address, suite, city, province, postal_code}. Pure + unit-tested.
 *
 * Each component: {longText, shortText, types: [...]}. Missing pieces → "".
 * province is administrative_area_level_1 SHORT text (e.g. "NS").
 */
export function parseAddressComponents(components: unknown): GoogleAddressRecord {
  const list = Array.isArray(components) ? components : [];
  const byType = (type: string) => list.find((c: any) => c && Array.isArray(c.types) && c.types.includes(type));

  const streetNumber = byType("street_number");
  const route = byType("route");
  const subpremise = byType("subpremise");
  const locality = byType("locality");
  const postalTown = byType("postal_town");
  const sublocality = byType("sublocality");
  const admin1 = byType("administrative_area_level_1");
  const postal = byType("postal_code");

  const routeText = (route && route.longText) || "";
  const numberText = (streetNumber && streetNumber.longText) || "";
  const address = numberText && routeText
    ? `${numberText} ${routeText}`
    : routeText || numberText || "";

  const city =
    (locality && locality.longText) ||
    (postalTown && postalTown.longText) ||
    (sublocality && sublocality.longText) ||
    "";

  return {
    address,
    suite: (subpremise && subpremise.longText) || "",
    city,
    province: (admin1 && admin1.shortText) || "",
    postal_code: (postal && postal.longText) || "",
  };
}
